import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  Crown, Heart, Trophy, Target, Users, MessageCircle, 
  FileText, BookOpen, Calendar, Star, Check, ArrowRight,
  Award, TrendingUp, Gift, X, Sparkles
} from 'lucide-react';
import axios from 'axios';
import soundManager from '../utils/soundEffects';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PremiumPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showHearts, setShowHearts] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'annual'
  const [purchasing, setPurchasing] = useState(false);

  // Optimized falling hearts animation
  const [fallingHearts, setFallingHearts] = useState([]);
  
  useEffect(() => {
    // Generate hearts only once on mount
    const hearts = [];
    const heartEmojis = ['💕', '💖', '💗', '💓', '💞', '💝', '❤️'];
    
    // Reduced from 30 to 15 for better performance
    for (let i = 0; i < 15; i++) {
      const randomHeart = heartEmojis[Math.floor(Math.random() * heartEmojis.length)];
      const leftPosition = Math.random() * 100;
      const animationDelay = Math.random() * 2;
      const animationDuration = 3 + Math.random() * 2;
      const fontSize = 20 + Math.random() * 15;
      
      hearts.push({
        id: i,
        emoji: randomHeart,
        style: {
          left: `${leftPosition}%`,
          animationDelay: `${animationDelay}s`,
          animationDuration: `${animationDuration}s`,
          fontSize: `${fontSize}px`,
          opacity: 0.7
        }
      });
    }
    
    setFallingHearts(hearts);
    
    // Remove hearts from DOM after 5 seconds to free up memory
    const timer = setTimeout(() => {
      setShowHearts(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, []); // Empty dependency array - only run once on mount

  const handlePurchase = async (tier) => {
    if (!user) {
      navigate('/auth');
      return;
    }
    
    // Play success sound
    soundManager.playSuccess();
    
    setPurchasing(true);
    
    try {
      // Determine package_id based on tier and billing cycle
      let packageId;
      if (tier === 'popular') {
        packageId = billingCycle === 'monthly' ? 'popular_monthly' : 'popular_annual';
      } else if (tier === 'premium') {
        packageId = billingCycle === 'monthly' ? 'premium_monthly' : 'premium_annual';
      }
      
      // Get current origin for success/cancel URLs
      const originUrl = window.location.origin;
      const token = localStorage.getItem('token');
      
      // Create Stripe checkout session
      const response = await axios.post(
        `${API}/payments/checkout/session?package_id=${packageId}&origin_url=${encodeURIComponent(originUrl)}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success && response.data.url) {
        // Play completion sound before redirect
        soundManager.playTestComplete();
        
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create payment session. Please try again.');
      console.error('Failed to create payment session:', error);
      setPurchasing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-purple-50 to-pink-50 relative overflow-hidden">
      {/* Optimized Falling Hearts Animation */}
      {showHearts && fallingHearts.length > 0 && (
        <div className="hearts-container pointer-events-none">
          {fallingHearts.map((heart) => (
            <div
              key={heart.id}
              className="falling-heart"
              style={heart.style}
            >
              {heart.emoji}
            </div>
          ))}
        </div>
      )}

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-card mx-6 mt-4 px-6 py-4">
        <div className="flex justify-between items-center">
          <div 
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <Heart className="w-8 h-8 text-rose-600" />
            <span className="text-2xl font-bold gradient-text">LuvCup</span>
          </div>
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-rose-600 font-medium transition-colors"
            >
              Home
            </button>
            {user ? (
              <button 
                onClick={() => navigate('/dashboard')}
                className="btn-secondary"
              >
                Dashboard
              </button>
            ) : (
              <button 
                onClick={() => navigate('/auth')}
                className="btn-secondary"
              >
                Login
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center justify-center space-x-2 mb-6">
            <Crown className="w-12 h-12 text-yellow-500" />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold gradient-text mb-6">
            Choose Your Plan
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Transform your relationship journey with the perfect plan for you
          </p>
          
          {/* Billing Cycle Toggle */}
          <div className="flex items-center justify-center mb-12">
            <div className="bg-white rounded-full p-2 shadow-lg inline-flex">
              <button
                onClick={() => {
                  soundManager.playSuccess();
                  setBillingCycle('monthly');
                }}
                className={`px-8 py-3 rounded-full font-semibold transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-gradient-to-r from-rose-500 to-purple-600 text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => {
                  soundManager.playSuccess();
                  setBillingCycle('annual');
                }}
                className={`px-8 py-3 rounded-full font-semibold transition-all relative ${
                  billingCycle === 'annual'
                    ? 'bg-gradient-to-r from-rose-500 to-purple-600 text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Annual
                <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                  Save 33%
                </span>
              </button>
            </div>
          </div>
          
          {/* 3-Tier Pricing Comparison */}
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            
            {/* Free Plan */}
            <div className="bg-white rounded-2xl border-2 border-gray-200 p-8 hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Free</h3>
              <div className="flex items-baseline justify-center mb-6">
                <span className="text-5xl font-bold text-gray-900">$0</span>
                <span className="text-gray-600 ml-2">/month</span>
              </div>
              
              <ul className="space-y-4 mb-8 text-left">
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">5 basic relationship tests</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Basic AI insights</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Test history & results</span>
                </li>
                <li className="flex items-start space-x-3">
                  <X className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400">Limited retakes (1/month)</span>
                </li>
                <li className="flex items-start space-x-3">
                  <X className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400">No PDF reports</span>
                </li>
                <li className="flex items-start space-x-3">
                  <X className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400">No premium features</span>
                </li>
              </ul>
              
              <button 
                onClick={() => navigate('/auth')}
                className="w-full py-3 border-2 border-gray-300 text-gray-700 rounded-full font-semibold hover:bg-gray-50 transition-colors"
              >
                Get Started Free
              </button>
            </div>

            {/* Popular Plan (Most Popular) */}
            <div className="bg-gradient-to-br from-rose-500 to-purple-600 rounded-2xl p-8 transform scale-105 shadow-2xl relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold">
                  MOST POPULAR
                </span>
              </div>
              
              <h3 className="text-2xl font-bold text-white mb-2">Popular</h3>
              <div className="flex items-baseline justify-center mb-6">
                {billingCycle === 'annual' ? (
                  <>
                    <span className="text-5xl font-bold text-white">$39</span>
                    <span className="text-white/90 ml-2">/year</span>
                  </>
                ) : (
                  <>
                    <span className="text-5xl font-bold text-white">$4.99</span>
                    <span className="text-white/90 ml-2">/month</span>
                  </>
                )}
              </div>
              {billingCycle === 'annual' && (
                <p className="text-white/80 text-sm text-center mb-4">
                  Just $3.25/month • Save $20.88/year
                </p>
              )}
              
              <ul className="space-y-4 mb-8 text-left">
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                  <span className="text-white">Everything in Free</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                  <span className="text-white">Unlimited test retakes</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                  <span className="text-white">Advanced AI insights</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                  <span className="text-white">Basic PDF reports</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                  <span className="text-white">Couples matching</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                  <span className="text-white">Priority support</span>
                </li>
              </ul>
              
              <button 
                onClick={() => handlePurchase('popular')}
                disabled={purchasing}
                className="w-full py-3 bg-white text-rose-600 rounded-full font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {purchasing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-rose-600 border-t-transparent"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Crown className="w-5 h-5" />
                    <span>Upgrade Now</span>
                  </>
                )}
              </button>
              <p className="text-white/80 text-xs mt-4">7-day free trial • Cancel anytime</p>
            </div>

            {/* Premium Plan */}
            <div className="bg-white rounded-2xl border-2 border-purple-200 p-8 hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Premium</h3>
              <div className="flex items-baseline justify-center mb-6">
                {billingCycle === 'annual' ? (
                  <>
                    <span className="text-5xl font-bold text-gray-900">$79</span>
                    <span className="text-gray-600 ml-2">/year</span>
                  </>
                ) : (
                  <>
                    <span className="text-5xl font-bold text-gray-900">$9.99</span>
                    <span className="text-gray-600 ml-2">/month</span>
                  </>
                )}
              </div>
              {billingCycle === 'annual' && (
                <p className="text-gray-600 text-sm text-center mb-4">
                  Just $6.58/month • Save $40.89/year
                </p>
              )}
              
              <ul className="space-y-4 mb-8 text-left">
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Everything in Popular</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Professional PDF reports</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">1-on-1 counseling session</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Exclusive courses library</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Community forum access</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">VIP badge & benefits</span>
                </li>
              </ul>
              
              <button 
                onClick={() => handlePurchase('premium')}
                disabled={purchasing}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-full font-semibold hover:from-purple-700 hover:to-purple-800 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {purchasing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Star className="w-5 h-5" />
                    <span>Go Premium</span>
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="mt-12 flex items-center justify-center space-x-6 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <Check className="w-4 h-4 text-green-500" />
              <span>Cancel anytime</span>
            </div>
            <div className="flex items-center space-x-2">
              <Check className="w-4 h-4 text-green-500" />
              <span>30-day money-back</span>
            </div>
            <div className="flex items-center space-x-2">
              <Check className="w-4 h-4 text-green-500" />
              <span>Secure payments</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center gradient-text mb-16">
            Premium Features
          </h2>

          {/* Gamification Features */}
          <div className="glass-card p-8 mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <Trophy className="w-8 h-8 text-yellow-500" />
              <h3 className="text-2xl font-bold text-gray-800">Gamification & Achievements</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex flex-col items-start">
                <Award className="w-10 h-10 text-rose-500 mb-3" />
                <h4 className="font-semibold text-lg mb-2">Exclusive Badges</h4>
                <p className="text-gray-600">Earn unique badges for completing tests, streaks, and milestones. Show off your relationship growth!</p>
              </div>
              <div className="flex flex-col items-start">
                <TrendingUp className="w-10 h-10 text-purple-500 mb-3" />
                <h4 className="font-semibold text-lg mb-2">Leaderboards</h4>
                <p className="text-gray-600">Compete with others (anonymously) and see how your relationship skills stack up globally.</p>
              </div>
              <div className="flex flex-col items-start">
                <Target className="w-10 h-10 text-blue-500 mb-3" />
                <h4 className="font-semibold text-lg mb-2">Daily Challenges</h4>
                <p className="text-gray-600">New daily relationship challenges to improve communication, empathy, and connection.</p>
              </div>
            </div>
          </div>

          {/* Social Features */}
          <div className="glass-card p-8 mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <Users className="w-8 h-8 text-blue-500" />
              <h3 className="text-2xl font-bold text-gray-800">Social Features</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex flex-col items-start">
                <Heart className="w-10 h-10 text-rose-500 mb-3" />
                <h4 className="font-semibold text-lg mb-2">Couples Matching</h4>
                <p className="text-gray-600">Connect with your partner and compare test results. Get personalized couple insights and compatibility scores.</p>
              </div>
              <div className="flex flex-col items-start">
                <MessageCircle className="w-10 h-10 text-green-500 mb-3" />
                <h4 className="font-semibold text-lg mb-2">Community Forums</h4>
                <p className="text-gray-600">Join private forums to share experiences, ask questions, and get advice from relationship experts and peers.</p>
              </div>
            </div>
          </div>

          {/* Premium Subscriptions */}
          <div className="glass-card p-8 mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <Star className="w-8 h-8 text-yellow-500" />
              <h3 className="text-2xl font-bold text-gray-800">Premium Access</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex flex-col items-start">
                <Sparkles className="w-10 h-10 text-purple-500 mb-3" />
                <h4 className="font-semibold text-lg mb-2">Advanced AI Insights</h4>
                <p className="text-gray-600">Get deeper, more personalized AI-powered insights with actionable recommendations for every test.</p>
              </div>
              <div className="flex flex-col items-start">
                <FileText className="w-10 h-10 text-blue-500 mb-3" />
                <h4 className="font-semibold text-lg mb-2">Unlimited Tests</h4>
                <p className="text-gray-600">Take all available tests unlimited times. Track your progress over time with detailed history.</p>
              </div>
            </div>
          </div>

          {/* One-Time Purchases */}
          <div className="glass-card p-8 mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <Gift className="w-8 h-8 text-rose-500" />
              <h3 className="text-2xl font-bold text-gray-800">Premium Add-Ons</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex flex-col items-start">
                <FileText className="w-10 h-10 text-rose-500 mb-3" />
                <h4 className="font-semibold text-lg mb-2">Detailed PDF Reports</h4>
                <p className="text-gray-600">Professional-grade PDF reports with charts, graphs, and comprehensive analysis you can share with therapists.</p>
              </div>
              <div className="flex flex-col items-start">
                <Calendar className="w-10 h-10 text-purple-500 mb-3" />
                <h4 className="font-semibold text-lg mb-2">Counseling Sessions</h4>
                <p className="text-gray-600">Book one-on-one video sessions with certified relationship counselors directly through the platform.</p>
              </div>
            </div>
          </div>

          {/* Affiliate Partnerships */}
          <div className="glass-card p-8 mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <BookOpen className="w-8 h-8 text-green-500" />
              <h3 className="text-2xl font-bold text-gray-800">Exclusive Resources</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex flex-col items-start">
                <BookOpen className="w-10 h-10 text-green-500 mb-3" />
                <h4 className="font-semibold text-lg mb-2">Curated Book Library</h4>
                <p className="text-gray-600">Access exclusive discounts on top relationship books handpicked by our experts. Build your love library!</p>
              </div>
              <div className="flex flex-col items-start">
                <Star className="w-10 h-10 text-yellow-500 mb-3" />
                <h4 className="font-semibold text-lg mb-2">Expert Courses</h4>
                <p className="text-gray-600">Special pricing on premium relationship courses from world-renowned relationship coaches and therapists.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 px-6 bg-gradient-to-r from-rose-500 to-purple-500">
        <div className="max-w-4xl mx-auto text-center">
          <Crown className="w-16 h-16 text-white mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Relationships?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join thousands of couples who have improved their relationships with LuvCup Premium
          </p>
          <div className="glass-card bg-white/10 backdrop-blur-lg max-w-md mx-auto p-8 mb-8">
            <div className="flex items-baseline justify-center mb-4">
              <span className="text-6xl font-bold text-white">$4.99</span>
              <span className="text-xl text-white/90 ml-2">/month</span>
            </div>
            <button 
              onClick={() => handlePurchase('popular')}
              disabled={purchasing}
              className="bg-white text-rose-600 hover:bg-gray-100 font-semibold px-8 py-4 rounded-full transition-all duration-300 w-full text-lg flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {purchasing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-rose-600 border-t-transparent"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Crown className="w-5 h-5" />
                  <span>Get Premium Now</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
          <p className="text-white/80 text-sm">
            ✨ 7-day free trial • Cancel anytime • Money-back guarantee
          </p>
        </div>
      </section>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
          <div className="glass-card max-w-md w-full p-8 relative animate-fade-in">
            <button 
              onClick={() => setShowPaymentModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="text-center mb-6">
              <Crown className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold gradient-text mb-2">Complete Your Purchase</h3>
              <p className="text-gray-600">LuvCup Premium - $4.99/month</p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="p-4 bg-rose-50 rounded-lg border-2 border-rose-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">Premium Subscription</span>
                  <span className="font-bold text-rose-600">$4.99</span>
                </div>
                <p className="text-sm text-gray-600">Billed monthly, cancel anytime</p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>7-day free trial included</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>All premium features unlocked</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>30-day money-back guarantee</span>
                </div>
              </div>
            </div>

            <button 
              onClick={handleMockPayment}
              className="btn-primary w-full text-lg py-4 flex items-center justify-center space-x-2"
            >
              <span>Complete Purchase (Mock)</span>
            </button>
            
            <p className="text-xs text-center text-gray-500 mt-4">
              This is a mock payment. No real charges will be made.
            </p>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
          <div className="glass-card max-w-md w-full p-8 text-center animate-fade-in">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-12 h-12 text-green-500" />
            </div>
            <h3 className="text-2xl font-bold gradient-text mb-2">Welcome to Premium! 🎉</h3>
            <p className="text-gray-600 mb-6">
              Your account has been upgraded. Enjoy all premium features!
            </p>
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-rose-500 border-t-transparent"></div>
              <span className="text-sm text-gray-600">Redirecting to dashboard...</span>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Heart className="w-8 h-8 text-rose-500" />
            <span className="text-2xl font-bold">LuvCup</span>
          </div>
          <p className="text-gray-400 mb-6">
            Transform your relationships with AI-powered insights
          </p>
          <div className="flex justify-center space-x-6 mb-6">
            <button 
              onClick={() => navigate('/privacy')}
              className="text-gray-400 hover:text-white transition-colors"
            >
              Privacy Policy
            </button>
            <button 
              onClick={() => navigate('/terms')}
              className="text-gray-400 hover:text-white transition-colors"
            >
              Terms of Service
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="text-gray-400 hover:text-white transition-colors"
            >
              Contact
            </button>
          </div>
          <p className="text-gray-500 text-sm">
            © 2025 LuvCup. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PremiumPage;
